import {h, Fragment} from "preact"
import { useFacets, useProductFilters } from "@nosto/preact"
import { useEffect } from "preact/hooks"
import Facet from "./Facet"
import RangeFacet from "./RangeFacet"
import Icon from "./elements/Icon"
import SortDropdown from "./elements/SortDropdown"

export const toggleButtonId = "toggle-mobile-sidebar"

function ToggleSidebarButton({ className }) {
    return (
        <button
        // ns-absolute
            type="button"
            className={`ns-act-btn ns-close-sidebar ns-d-block ns-d-md-none ns-text-align-right ns-align-items-center ns-clickable ns-font-3 ns-p-0 ${className}`}
            aria-label="Close filters"
            onClick={() => {
                const sidebarToggle = document.getElementById(toggleButtonId);
                if (sidebarToggle) {
                    sidebarToggle.click();
                }
            }}
            tabIndex="0"
        >
            <Icon name="close" />
        </button>
    )
}

export default function SideBar({ toggledFacet, setToggledFacet, setIsChecked }) {
    const { loading, facets } = useFacets()
    const { removeAll } = useProductFilters()

    // Comprehensive focus trap for modal
    useEffect(() => {
        let isModalOpen = false;
        let allEventListeners = [];
        let focusMonitorInterval = null;
        
        const createEventHandler = (eventType, handler, options = false) => {
            document.addEventListener(eventType, handler, options);
            allEventListeners.push({ eventType, handler, options });
        };
        
        const removeAllEventListeners = () => {
            allEventListeners.forEach(({ eventType, handler, options }) => {
                document.removeEventListener(eventType, handler, options);
            });
            allEventListeners = [];
        };
        
        const getFocusableElements = () => {
            const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
            if (!modal) return [];
            
            // Don't check for ns-d-none class since CSS might override it
            // Instead check computed display style
            const modalStyle = window.getComputedStyle(modal);
            if (modalStyle.display === 'none' || modalStyle.visibility === 'hidden') {
                return [];
            }
            
            const focusableSelectors = [
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])',
                '[role="button"]:not([disabled])',
                '[role="checkbox"]',
                '[role="radio"]'
            ];
            
            const elements = Array.from(modal.querySelectorAll(focusableSelectors.join(','))).filter(el => {
                const style = window.getComputedStyle(el);
                return style.display !== 'none' && 
                    style.visibility !== 'hidden' && 
                    el.offsetParent !== null &&
                    !el.hasAttribute('aria-hidden') &&
                    !el.hasAttribute('data-sidebar-disabled'); // Don't include disabled elements
            });
            
            return elements;
        };
        
        const disableOutsideElements = () => {
            const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
            if (!modal) {
                console.log('❌ Modal not found for disabling outside elements');
                return;
            }
            
            // Disable all focusable elements outside the modal
            const allFocusable = document.querySelectorAll(
                'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"]), [role="button"], [role="link"], [role="tab"], [role="option"]'
            );
            
            let disabledCount = 0;
            allFocusable.forEach(el => {
                // Skip if element is inside the modal
                if (modal.contains(el)) return;
                
                // Skip the toggle button itself
                if (el.id === 'toggle-mobile-sidebar') return;
                
                // Store original state and disable
                if (!el.hasAttribute('data-sidebar-disabled')) {
                    const originalTabindex = el.getAttribute('tabindex');
                    el.setAttribute('data-sidebar-original-tabindex', originalTabindex || 'none');
                    el.setAttribute('data-sidebar-disabled', 'true');
                    el.setAttribute('tabindex', '-1');
                    
                    // Use inert if supported
                    if ('inert' in el) {
                        el.inert = true;
                    }
                    disabledCount++;
                }
            });
            

        };
        
        const restoreOutsideElements = () => {
            document.querySelectorAll('[data-sidebar-disabled]').forEach(el => {
                const originalTabindex = el.getAttribute('data-sidebar-original-tabindex');
                
                el.removeAttribute('data-sidebar-disabled');
                el.removeAttribute('data-sidebar-original-tabindex');
                
                if (originalTabindex === 'none') {
                    el.removeAttribute('tabindex');
                } else {
                    el.setAttribute('tabindex', originalTabindex);
                }
                
                if ('inert' in el) {
                    el.inert = false;
                }
            });
        };
        
        const forceFocusInModal = () => {
            if (!isModalOpen) return;
            
            const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
            const currentFocus = document.activeElement;
            
            // If focus escaped the modal, force it back
            if (!modal || !modal.contains(currentFocus) || currentFocus === document.body) {
                const focusableElements = getFocusableElements();
                if (focusableElements.length > 0) {
                    focusableElements[0].focus();
                }
            }
        };
        
        const updateModalState = () => {
            const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
            const toggle = document.getElementById('toggle-mobile-sidebar');
            const backdrop = document.querySelector('.ns-sidebar-backdrop');
            const isOpen = toggle && toggle.checked;
            const bodyHasSidebarClass = document.body.classList.contains('ns-sidebar-opened');
            

            
            // More comprehensive visibility check - use computed styles instead of classes
            let isVisible = false;
            if (modal) {
                const computedStyle = window.getComputedStyle(modal);
                const hasDisplayNone = modal.classList.contains('ns-d-none');
                const hasVisibilityHidden = computedStyle.visibility === 'hidden';
                const hasDisplayNoneStyle = computedStyle.display === 'none';
                
                // Use actual computed styles rather than classes
                isVisible = isOpen && computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
                

            }
            

            
            if (isOpen && isVisible && !isModalOpen) {
                // Opening
                isModalOpen = true;
                
                // Disable outside elements
                disableOutsideElements();
                
                // Set up comprehensive focus trapping
                createEventHandler('keydown', (e) => {
                    if (!isModalOpen) return;
                    
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        closeSidebar();
                        return false;
                    }
                    
                    if (e.key === 'Tab') {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        
                        const focusableElements = getFocusableElements();
                        
                        if (focusableElements.length === 0) {
                            return false;
                        }
                        
                        const currentIndex = focusableElements.indexOf(document.activeElement);
                        let nextIndex;
                        
                        if (e.shiftKey) {
                            nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
                        } else {
                            nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
                        }
                        
                        focusableElements[nextIndex].focus();
                        return false;
                    }
                }, true);
                
                // Monitor focus changes
                createEventHandler('focusin', (e) => {
                    if (!isModalOpen) return;
                    
                    const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
                    if (!modal || !modal.contains(e.target)) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        forceFocusInModal();
                    }
                }, true);
                
                // Continuous monitoring
                focusMonitorInterval = setInterval(forceFocusInModal, 100);
                
            } else if ((!isOpen || !isVisible) && isModalOpen) {
                // Closing
                isModalOpen = false;
                
                // Clean up
                removeAllEventListeners();
                restoreOutsideElements();
                
                if (focusMonitorInterval) {
                    clearInterval(focusMonitorInterval);
                    focusMonitorInterval = null;
                }
            }
        };
        
        // Monitor for sidebar state changes
        const observer = new MutationObserver(updateModalState);
        observer.observe(document.body, { 
            childList: true, 
            subtree: true, 
            attributes: true, 
            attributeFilter: ['class', 'checked'] 
        });
        
        // Also listen directly to the checkbox
        const toggle = document.getElementById('toggle-mobile-sidebar');
        let handleToggleChange = null;
        
        if (toggle) {
            handleToggleChange = () => {
                setTimeout(updateModalState, 10); // Small delay to let CSS changes apply
            };
            toggle.addEventListener('change', handleToggleChange);
        }
        
        // Initial check
        updateModalState();
        
        return () => {
            removeAllEventListeners();
            restoreOutsideElements();
            if (focusMonitorInterval) {
                clearInterval(focusMonitorInterval);
            }
            observer.disconnect();
            
            // Clean up checkbox listener
            if (toggle && handleToggleChange) {
                toggle.removeEventListener('change', handleToggleChange);
            }
        };
    }, []);

    function closeSidebar() {
        const closeBtn = document.querySelector('.ns-close-sidebar');
        if(closeBtn){
            closeBtn.click();
        }
        
        // Return focus to the button that opened the modal
        setTimeout(() => {
            const triggerButton = document.querySelector('.ns-filters');
            if (triggerButton) {
                triggerButton.focus();
            }
        }, 100);
    }

    function onInputChange(e) {
        const input = e.target;
        // console.log(input.checked, 'input checked');
        setIsChecked(input.checked);
        
        // Focus management for modal
        if (input.checked) {
            // Modal opened - move focus to first focusable element
            setTimeout(() => {
                const modal = document.querySelector('.ns-sidebar-wrapper[role="dialog"]');
                if (modal) {
                    const firstFocusable = modal.querySelector('button, [tabindex]:not([tabindex="-1"])');
                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                }
            }, 100);
        }
    }

    return facets?.length > 0 ? (
        <>
            <input type="checkbox" id={toggleButtonId} class="ns-facet-sidebar-toggle ns-d-none" onChange={(e)=>onInputChange(e)} />
            <label
                class="ns-sidebar-backdrop ns-fixed ns-w-100 ns-h-100 ns-background-black ns-d-none"
                for={toggleButtonId}
            />
            <div
            //  ns-col-3 ns-col-l-2 ns-d-md-block
                class="ns-sidebar-wrapper ns-d-none ns-h-100"
                role="dialog"
                aria-modal="true"
                aria-label="Filters"
                // style={loading ? "opacity: 0.3;" : ""}
            >
                {/* ns-p-4 */}
                <div class="ns-sidebar-content ns-relative ns-background-white ns-h-100 ns-mr-0 ns-mr-md-auto">
                    <div class="ns-sidebar-header ns-d-flex ns-justify-content-between ns-align-items-center">
                        <h2 class="ns-font-medium ns-font-5 ns-color-black">FILTERS</h2>
                        <ToggleSidebarButton />
                    </div>
                    <div class="ns-sidebar-menu">
                    {/* ns-d-flex */}
                        <ul class="ns-list-unstyled ns-p-0 ns-m-0" tabindex="0" id="nosto-facets-container">
                            <SortDropdown toggledFacet={toggledFacet} setToggledFacet={setToggledFacet}/>
                            {facets?.map(facet => {
                                switch (facet.type) {
                                    case "terms":
                                        return <Facet key={facet.id} facet={facet} toggledFacet={toggledFacet} setToggledFacet={setToggledFacet}/>
                                    case "stats":
                                        return <RangeFacet key={facet.id} facet={facet} toggledFacet={toggledFacet} setToggledFacet={setToggledFacet}/>
                                    default:
                                        return null
                                }
                            })}
                        </ul>
                    </div>
                    <div class="ns-sidebar-footer ns-w-100 flex gap-2 px-5 py-4 border-t justify-center border-grey-3">
                        <button
                        // ns-col-2 ns-text-align-right ns-color-link ns-font-4 ns-ml-2 ns-mr-0 ns-ml-auto
                        class="ns-anchor ns-act-btn"
                        type="button"
                        onClick={() => {
                            removeAll()
                        }}
                        aria-label="Clear all filters"
                    >
                        CLEAR ALL
                    </button>
                    <button class="ns-apply-btn ns-act-btn" type="button" onClick={closeSidebar} aria-label="Apply filters and close modal">APPLY</button>
                    </div>
                </div>
            </div>
        </>
    ) : (
        ""
    )
}
